import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useLocaleStore, type SupportedLocale } from '../store/locale-store';

interface LanguageOption {
  code: SupportedLocale;
  label: string;
  flag: string;
  shortCode: string; // 2-letter code for display in compact view
}

const languages: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇬🇧', shortCode: 'EN' },
  { code: 'en-US', label: 'English (US)', flag: '🇺🇸', shortCode: 'US' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰', shortCode: 'DA' },
  { code: 'es', label: 'Español', flag: '🇪🇸', shortCode: 'ES' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', shortCode: 'FR' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', shortCode: 'DE' },
];

/**
 * Language switcher component
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const setLocale = useLocaleStore((s) => s.setLocale);

  // Hide language switcher when language_mode=fixed
  const languageMode = searchParams.get('language_mode');
  if (languageMode === 'fixed') {
    return null;
  }

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) ?? languages[0]!;

  const handleLanguageChange = (code: SupportedLocale) => {
    setLocale(code);
    void i18n.changeLanguage(code);
  };

  return (
    <div className="relative group">
      {/* Current language button */}
      <button
        className="surface-float flex h-11 items-center gap-2 rounded-full px-3.5 hover:shadow-monta-lg transition-shadow"
        aria-label="Change language"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="text-sm font-medium text-ink min-w-[1.5rem] text-center">
          {currentLanguage.shortCode}
        </span>
        <svg
          className="w-4 h-4 text-muted transition-transform group-hover:rotate-180"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      <div className="absolute top-full mt-2 right-0 w-48 bg-surface text-ink rounded-2xl shadow-monta-lg ring-1 ring-ink/10 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
        <div className="py-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                lang.code === i18n.language
                  ? 'bg-ink/5 text-ink'
                  : 'hover:bg-ink/5 text-ink/80'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.label}</span>
              {lang.code === i18n.language && (
                <svg
                  className="w-4 h-4 ml-auto text-ink"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact language switcher for mobile
 */
export function LanguageSwitcherCompact() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const setLocale = useLocaleStore((s) => s.setLocale);

  // Hide language switcher when language_mode=fixed
  const languageMode = searchParams.get('language_mode');
  if (languageMode === 'fixed') {
    return null;
  }

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) ?? languages[0]!;
  const otherLanguage =
    languages.find((lang) => lang.code !== i18n.language) ?? languages[1]!;

  const handleToggle = () => {
    setLocale(otherLanguage.code);
    void i18n.changeLanguage(otherLanguage.code);
  };

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-full shadow-monta-sm hover:shadow-monta-md transition-shadow border border-monta-gray-200"
      aria-label={`Switch to ${otherLanguage.label}`}
    >
      <span className="text-base">{currentLanguage.flag}</span>
      <svg
        className="w-3 h-3 text-monta-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
      <span className="text-base">{otherLanguage.flag}</span>
    </button>
  );
}
